<template>
  <v-container>
    <!-- ✅ Success Card -->
    <v-card
      v-if="savedLocation"
      outlined
      rounded="xl"
      class="pa-5 text-center elevation-2"
    >
      <v-icon color="success" large class="mb-3"
        >mdi-check-circle-outline</v-icon
      >
      <h2 class="font-weight-bold mb-2">Location Created Successfully</h2>
      <p class="grey--text text--darken-1">
        An employee with your name has been automatically created for this
        store. You can now log in to the store and continue setup.
      </p>
      <v-card-actions class="justify-center mt-4">
        <v-btn
          outlined
          color="primary"
          @click="$router.push({ path: '/location' })"
        >
          <v-icon left small>mdi-arrow-left</v-icon> Back to Locations
        </v-btn>
        <v-btn
          color="primary"
          dark
          rounded
          target="_blank"
          :href="`${terminalURL}/login/location?code=${savedLocation.code}`"
        >
          <v-icon left small>mdi-open-in-new</v-icon> Open New Location
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- ✅ Create Form -->
    <v-card v-else outlined rounded="xl" class="pa-6 elevation-1">
      <!-- Header -->
      <v-toolbar flat dense color="transparent" class="mb-4 pa-0">
        <v-toolbar-title>
          <h2 class="font-weight-bold">Business Information</h2>
          <div class="subtitle-2 grey--text text--darken-1 mt-1">
            Enter your business details below.
            <strong
              >This information will appear on your receipts and
              invoices.</strong
            >
          </div>
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <!-- Business Info -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.name"
              label="Business Name"
              outlined
              :error-messages="locationNameErrors"
              hint="e.g. Main Street Wireless LLC, El Primo Repairs"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.website"
              label="Website"
              outlined
              placeholder="https://example.com"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.DCALicense"
              label="DCA License"
              outlined
              placeholder="Optional"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.email"
              label="Email"
              outlined
              :error-messages="locationEmailErrors"
              placeholder="e.g. info@business.com"
            />
          </v-col>
        </v-row>

        <!-- Additional Info -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.type"
              label="Business Type"
              outlined
              placeholder="e.g. Retail, Auto Repair"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.timeZone"
              :items="timeZones"
              item-text="label"
              item-value="tzCode"
              label="Time Zone"
              outlined
            />
          </v-col>
        </v-row>

        <!-- Address Section -->
        <v-divider class="my-6" />
        <h3 class="subtitle-1 font-weight-bold mb-3">Address Information</h3>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.type"
              label="Address Type"
              outlined
              placeholder="e.g. Business, Office, Home"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.phoneNumber"
              label="Phone Number"
              outlined
              placeholder="(555) 123-4567"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.addressLine1"
              label="Address Line 1"
              outlined
              :error-messages="addressLine1Errors"
              placeholder="Street address, P.O. box"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.addressLine2"
              label="Address Line 2"
              outlined
              placeholder="Apt, suite, unit, building, floor, etc."
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.city"
              label="City"
              outlined
              :error-messages="cityErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.Address.StateId"
              :items="states"
              item-text="name"
              item-value="id"
              label="State"
              outlined
              :error-messages="stateIdErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.postalCode"
              label="Zip / Postal Code"
              outlined
              :error-messages="postalCodeErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.Address.CountryCode"
              :items="countries"
              item-text="name"
              item-value="code"
              label="Country"
              outlined
              :error-messages="countryCodeErrors"
              @change="countrySelectionTouched = true"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="Location.Address.description"
              label="Description / Notes"
              outlined
              auto-grow
              placeholder="Any additional notes about this location"
            />
          </v-col>
        </v-row>

        <v-divider class="my-6" />
        <h3 class="subtitle-1 font-weight-bold mb-1">Billing plan</h3>
        <p class="text-body-2 text--secondary mb-4">Choose the plan for this location.</p>
        <v-progress-linear v-if="plansLoading" indeterminate color="primary" aria-label="Loading billing plans" class="mb-4" />
        <v-alert v-if="planError" type="error" outlined dense role="alert">{{ planError }} <v-btn text small color="error" @click="fetchPlans">Retry</v-btn></v-alert>
        <v-alert v-else-if="!plansLoading && !plans.length" type="info" outlined dense>No active billing plans are available for this brand. Contact support before creating this location.</v-alert>
        <v-row v-else aria-label="Available billing plans">
          <v-col v-for="plan in plans" :key="plan.id || plan.planId || plan.code" cols="12" sm="6" lg="4" class="d-flex">
            <v-card outlined class="plan-card flex-grow-1" :class="{ 'plan-card--selected': isSelectedPlan(plan) }" role="button" tabindex="0" :aria-pressed="isSelectedPlan(plan) ? 'true' : 'false'" @click="selectPlan(plan)" @keydown.enter.prevent="selectPlan(plan)" @keydown.space.prevent="selectPlan(plan)">
              <v-card-text><div class="d-flex align-start"><div><div class="text-subtitle-1 font-weight-bold">{{ plan.name }}</div><div class="text-h6 font-weight-bold mt-1">{{ planPrice(plan) }}</div></div><v-spacer /><v-icon :color="isSelectedPlan(plan) ? 'primary' : undefined" aria-hidden="true">{{ isSelectedPlan(plan) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon></div><div v-if="plan.description" class="text-body-2 text--secondary mt-3">{{ plan.description }}</div><v-chip small outlined class="mt-3" :color="isSelectedPlan(plan) ? 'primary' : undefined">{{ trialLabel(plan) }}</v-chip></v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-alert v-if="planTouched && plans.length && !selectedPlan" type="error" outlined dense role="alert">Select a billing plan to continue.</v-alert>

        <div v-if="paidPlan" class="mt-6">
          <v-divider class="mb-6" />
          <h3 class="subtitle-1 font-weight-bold mb-1">Payment method</h3>
          <p class="text-body-2 text--secondary mb-4">Choose a saved card or securely enter a new card for this location’s subscription.</p>
          <v-progress-linear v-if="paymentLoading" indeterminate color="primary" aria-label="Loading saved payment methods" class="mb-4" />
          <v-alert v-if="paymentError" type="error" outlined dense role="alert">{{ paymentError }} <v-btn text small color="error" @click="loadPaymentMethods">Retry</v-btn></v-alert>
          <template v-else-if="!paymentLoading">
            <v-radio-group v-model="paymentChoice" aria-label="Payment method">
              <v-radio v-for="method in paymentMethods" :key="method.id" :value="method.id">
                <template #label><span>{{ method.brand || 'Card' }} ending in {{ method.last4 }} · expires {{ expiryLabel(method) }}</span></template>
              </v-radio>
              <v-radio value="new" label="Add a new card" />
            </v-radio-group>
            <v-alert v-if="!paymentMethods.length" type="info" outlined dense>No saved cards are available. Add a new card to continue.</v-alert>
            <v-sheet v-if="paymentChoice === 'new'" outlined rounded class="pa-4">
              <div class="text-body-2 font-weight-medium mb-3">Enter the new card details below</div>
              <v-alert v-if="!stripePublicKey" type="error" outlined dense>Secure card entry is unavailable for this billing account.</v-alert>
              <Card v-else :key="stripePublicKey" :stripe="stripePublicKey" class="stripe-card" @change="cardComplete = $event.complete" />
            </v-sheet>
            <v-alert v-if="paymentTouched && !validPaymentChoice" type="error" outlined dense role="alert">{{ paymentValidationMessage }}</v-alert>
          </template>
        </div>
        <v-alert v-if="submitError" type="error" outlined dense role="alert" class="mt-4">{{ submitError }}</v-alert>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="d-flex justify-end py-4">
        <v-btn text color="grey darken-1" to="/location/list">
          <v-icon left>mdi-close</v-icon> Cancel
        </v-btn>
        <v-btn
          color="primary"
          dark
          rounded
          @click="submit"
          :loading="isLoading"
          :disabled="plansLoading || !!planError || !plans.length || (paidPlan && (paymentLoading || !!paymentError))"
        >
          <v-icon left>mdi-content-save-outline</v-icon> Create Location
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { Card, createToken } from "vue-stripe-elements-plus";
import timezones from "compact-timezone-list";
import locationService from "../service.js";
import publicService from "../../../api/public";
import { unitedStatesCountryCode } from "@/config/defaultCountry";
const { validationMixin } = require("vuelidate");
const { required, minLength, email } = require("vuelidate/lib/validators");

export default {
  name: "location-create",
  components: { Card },
  data() {
    return {
      terminalURL: process.env.VUE_APP_TERMINALURL,
      isLoading: false,
      savedLocation: null,
      plans: [],
      selectedPlan: null,
      plansLoading: false,
      planError: "",
      planTouched: false,
      plansRequestId: 0,
      countrySelectionTouched: false,
      paymentMethods: [],
      paymentChoice: null,
      paymentLoading: false,
      paymentError: "",
      paymentTouched: false,
      stripePublicKey: "",
      cardComplete: false,
      paymentRequestId: 0,
      submitError: "",
      timeZones: timezones,
      Location: {
        name: null,
        type: null,
        website: null,
        DCALicense: null,
        timeZone: this.$moment.tz.guess(),
        email: null,
        Address: {
          type: null,
          addressLine1: null,
          addressLine2: null,
          city: null,
          StateId: null,
          CountryCode: null,
          postalCode: null,
          phoneNumber: null,
          description: null,
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", ["states", "countries"]),
    ...mapGetters("brand", ["currentBrand"]),
    paidPlan() { return !!(this.selectedPlan && this.selectedPlan.requiresCard !== false && Number(this.selectedPlan.amount) > 0); },
    validPaymentChoice() { return !this.paidPlan || (!!this.paymentChoice && (this.paymentChoice !== "new" || (this.stripePublicKey && this.cardComplete))); },
    paymentValidationMessage() { return this.paymentChoice === "new" ? "Complete the new card details before creating this location." : "Choose a saved card or add a new card."; },
    locationNameErrors() {
      const errors = [];
      if (!this.$v.Location.name.$dirty) return errors;
      if (!this.$v.Location.name.required)
        errors.push("Business name is required.");
      if (!this.$v.Location.name.minLength)
        errors.push("Name must be at least 4 characters long.");
      return errors;
    },
    locationEmailErrors() {
      const errors = [];
      if (!this.$v.Location.email.$dirty) return errors;
      if (!this.$v.Location.email.email) errors.push("Invalid email address.");
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.Location.Address.addressLine1.$dirty) return errors;
      if (!this.$v.Location.Address.addressLine1.required)
        errors.push("Address is required.");
      if (!this.$v.Location.Address.addressLine1.minLength)
        errors.push("Address must have at least 2 characters.");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.Location.Address.city.$dirty) return errors;
      if (!this.$v.Location.Address.city.required)
        errors.push("City is required.");
      return errors;
    },
    postalCodeErrors() {
      const errors = [];
      if (!this.$v.Location.Address.postalCode.$dirty) return errors;
      if (!this.$v.Location.Address.postalCode.required)
        errors.push("Postal code is required.");
      return errors;
    },
    stateIdErrors() {
      const errors = [];
      if (!this.$v.Location.Address.StateId.$dirty) return errors;
      if (!this.$v.Location.Address.StateId.required)
        errors.push("State is required.");
      return errors;
    },
    countryCodeErrors() {
      const errors = [];
      if (!this.$v.Location.Address.CountryCode.$dirty) return errors;
      if (!this.$v.Location.Address.CountryCode.required)
        errors.push("Country is required.");
      return errors;
    },
  },
  watch: {
    currentBrand() { this.clearPaymentContext(); this.selectedPlan = null; this.plans = []; this.fetchPlans(); },
    countries: {
      immediate: true,
      handler() { this.applyDefaultCountry(); },
    },
    paymentChoice(value, previous) { if (value !== previous) this.cardComplete = false; },
  },
  created() {
    this.fetchPlans();
  },
  methods: {
    applyDefaultCountry() {
      if (this.countrySelectionTouched || this.Location.Address.CountryCode != null) return;
      const code = unitedStatesCountryCode(this.countries);
      if (code != null) this.Location.Address.CountryCode = code;
    },
    planIdentifier(plan) { return plan && (plan.planId || plan.code); },
    isSelectedPlan(plan) { const id = this.planIdentifier(plan); return !!id && !!this.selectedPlan && this.planIdentifier(this.selectedPlan) === id; },
    selectPlan(plan) { if (!this.planIdentifier(plan)) { this.planError = "This billing plan is unavailable. Refresh the plans and choose another."; return; } this.clearPaymentContext(); this.submitError = ""; this.selectedPlan = plan; this.planTouched = true; if (this.paidPlan) this.loadPaymentMethods(); },
    clearPaymentContext() { this.paymentRequestId += 1; this.paymentMethods = []; this.paymentChoice = null; this.paymentLoading = false; this.paymentError = ""; this.paymentTouched = false; this.stripePublicKey = ""; this.cardComplete = false; },
    requestErrorBody(error) { return error && (error.data || (error.response && error.response.data)) || {}; },
    requestErrorMessage(error, fallback) { const body = this.requestErrorBody(error); return body.message || (body.error && body.error.message) || (error && error.message) || fallback; },
    expiryLabel(method) { return `${String(method.expMonth || "").padStart(2, "0")}/${method.expYear || ""}`; },
    uniquePaymentMethods(methods) { const ids = new Set(); return (Array.isArray(methods) ? methods : []).filter(method => { if (!method || !method.id || ids.has(method.id)) return false; ids.add(method.id); return true; }); },
    async loadPaymentMethods() { if (!this.paidPlan) return; const requestId = ++this.paymentRequestId; const selectedPlanId = this.planIdentifier(this.selectedPlan); this.paymentLoading = true; this.paymentError = ""; this.submitError = ""; try { const response = await locationService.Billing.getPaymentMethods(); if (requestId !== this.paymentRequestId || selectedPlanId !== this.planIdentifier(this.selectedPlan) || !this.paidPlan) return; const data = response.data || response; this.paymentMethods = this.uniquePaymentMethods(data.paymentMethods); this.stripePublicKey = data.publishableKey || ""; if (this.paymentMethods.length === 1) this.paymentChoice = this.paymentMethods[0].id; else if (!this.paymentMethods.length) this.paymentChoice = "new"; } catch (error) { if (requestId !== this.paymentRequestId) return; this.paymentError = this.requestErrorMessage(error, "Saved payment methods could not be loaded."); } finally { if (requestId === this.paymentRequestId) this.paymentLoading = false; } },
    planPrice(plan) { const amount = new Intl.NumberFormat(undefined, { style: "currency", currency: String(plan.currency || "USD").toUpperCase() }).format(Number(plan.amount || 0)); const count = Number(plan.intervalCount) || 1; const interval = plan.interval || "billing period"; return `${amount} every ${count > 1 ? `${count} ` : ""}${interval}${count > 1 ? "s" : ""}`; },
    trialLabel(plan) { const days = plan.trialDays == null ? 1 : Number(plan.trialDays); return days === 0 ? "No free trial" : `${days} free trial day${days === 1 ? "" : "s"}`; },
    async fetchPlans() { this.clearPaymentContext(); this.submitError = ""; const requestId = ++this.plansRequestId; const requestedBrand = this.currentBrand; this.plansLoading = true; this.planError = ""; this.selectedPlan = null; try { const response = await publicService.GetPlans({ audience: "LOCATION" }, requestedBrand); if (requestId !== this.plansRequestId || requestedBrand !== this.currentBrand) return; const rows = response.data || []; this.plans = rows.filter(plan => this.planIdentifier(plan)); if (rows.length && !this.plans.length) this.planError = "The available billing plans are missing required identifiers. Refresh the plans or contact support."; } catch (error) { if (requestId !== this.plansRequestId) return; this.plans = []; this.planError = "Billing plans could not be loaded for this brand."; } finally { if (requestId === this.plansRequestId) this.plansLoading = false; } },
    async submit() {
      if (this.isLoading) return;
      this.submitError = "";
      this.planTouched = true;
      const planId = this.planIdentifier(this.selectedPlan);
      if (!planId) return;
      this.paymentTouched = true;
      if (!this.validPaymentChoice || this.paymentLoading || this.paymentError) return;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          title: "Please fix the highlighted fields.",
          position: "top-end",
          timer: 2500,
        });
        return;
      }

      this.isLoading = true;
      try {
        this.Location.planId = planId;
        delete this.Location.paymentMethodId; delete this.Location.paymentSourceId; delete this.Location.paymentToken;
        if (this.paidPlan && this.paymentChoice === "new") { const result = await createToken(); if (result.error || !result.token || !result.token.id) { this.paymentError = (result.error && result.error.message) || "The new card could not be verified. Check the card details and try again."; return; } this.Location.paymentToken = result.token.id; }
        else if (this.paidPlan && String(this.paymentChoice).startsWith("pm_")) this.Location.paymentMethodId = this.paymentChoice;
        else if (this.paidPlan) this.Location.paymentSourceId = this.paymentChoice;
        const response = await locationService.create(this.Location);
        this.savedLocation = response.data;
        this.$swal.fire({
          toast: true,
          icon: "success",
          title: "Location created successfully.",
          position: "top-end",
          timer: 3000,
        });
      } catch (error) {
        const body = this.requestErrorBody(error);
        const message = this.requestErrorMessage(error, "Failed to create location. Please try again.");
        if (this.paidPlan && String(body.code || body.publicCode || "").startsWith("PAYMENT_")) this.paymentError = message;
        else this.submitError = message;
        this.$swal.fire({
          toast: true,
          icon: "error",
          title: message,
          position: "top-end",
          timer: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    Location: {
      name: { required, minLength: minLength(4) },
      email: { email },
      Address: {
        addressLine1: { required, minLength: minLength(2) },
        city: { required },
        postalCode: { required },
        StateId: { required },
        CountryCode: { required },
      },
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.25s ease;
}
.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.plan-card { cursor: pointer; }
.plan-card--selected { border-width: 2px; border-color: var(--v-primary-base); }
</style>
